import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  AvatarSmall,
  IconUserCircle,
  InlineTextButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarAccountMenu.module.css';

/**
 * The single account control in the top right corner, shared by the desktop and
 * mobile topbars so the two can't drift apart.
 *
 * The trigger has three faces:
 * - signed out: the outlined user-circle icon
 * - signed in with a profile image: that image (AvatarSmall)
 * - signed in without one: the same icon, filled with the forest-green CTA colour
 *
 * Signed out, the menu offers sign up / log in; signed in, it's the profile menu
 * that used to live in TopbarDesktop. Roles don't change the trigger — a seller is
 * a user with listings — they only add the "Your listings" item.
 *
 * The signed-in faces are held back until after mount, the same way TopbarDesktop
 * gates its own auth-dependent links: the server can know the visitor is authenticated
 * before currentUser has loaded, and rendering the avatar early would mismatch on
 * hydration. Both topbars pass the raw flag and let this component do that gating once.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {Function} props.onLogout
 * @param {boolean} props.showManageListingsLink
 * @param {number} props.notificationCount
 * @param {string} props.inboxTab
 * @param {Object} props.intl
 * @returns {JSX.Element} account menu
 */
const TopbarAccountMenu = props => {
  const {
    className,
    rootClassName,
    currentPage,
    currentUser,
    isAuthenticated,
    onLogout,
    showManageListingsLink,
    notificationCount = 0,
    inboxTab = 'orders',
    intl,
  } = props;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const showAuthenticated = mounted && isAuthenticated;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  // Same check Avatar makes internally, so the icon only stands in when Avatar
  // would otherwise fall back to initials.
  const hasProfileImage = !!ensureCurrentUser(currentUser).profileImage?.id;

  const trigger =
    showAuthenticated && hasProfileImage ? (
      <AvatarSmall className={css.avatar} user={currentUser} disableProfileLink />
    ) : (
      <IconUserCircle className={css.icon} isFilled={showAuthenticated} />
    );

  const ariaLabel = intl.formatMessage({
    id: showAuthenticated
      ? 'TopbarDesktop.screenreader.profileMenu'
      : 'TopbarDesktop.screenreader.accountMenu',
  });

  const notificationBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const authenticatedContent = [
    <MenuItem key="InboxPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass(`InboxPage:${inboxTab}`))}
        name="InboxPage"
        params={{ tab: inboxTab }}
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationBadge}
      </NamedLink>
    </MenuItem>,
    showManageListingsLink ? (
      <MenuItem key="ManageListingsPage">
        <NamedLink
          className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.yourListingsLink" />
        </NamedLink>
      </MenuItem>
    ) : null,
    <MenuItem key="ProfileSettingsPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
        name="ProfileSettingsPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
      </NamedLink>
    </MenuItem>,
    <MenuItem key="AccountSettingsPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
        name="AccountSettingsPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
      </NamedLink>
    </MenuItem>,
    <MenuItem key="logout">
      <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.logout" />
      </InlineTextButton>
    </MenuItem>,
  ];

  // Sign up leads, matching the emphasis the old filled signup button carried.
  const guestContent = [
    <MenuItem key="SignupPage">
      <NamedLink
        className={classNames(css.menuLink, css.menuLinkHighlight, currentPageClass('SignupPage'))}
        name="SignupPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.signup" />
      </NamedLink>
    </MenuItem>,
    <MenuItem key="LoginPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('LoginPage'))}
        name="LoginPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.login" />
      </NamedLink>
    </MenuItem>,
  ];

  return (
    <Menu
      className={classNames(rootClassName || css.root, className)}
      ariaLabel={ariaLabel}
      contentPosition="left"
    >
      <MenuLabel className={css.menuLabel}>{trigger}</MenuLabel>
      <MenuContent className={css.menuContent}>
        {showAuthenticated ? authenticatedContent : guestContent}
      </MenuContent>
    </Menu>
  );
};

export default TopbarAccountMenu;
